// Styled multi-select for table filters: uppercase label above, "All (n)" summary,
// checkbox list with "Select All". Same props/behaviour as MultiSelectWithAll.
import { useRef, useState } from "react";
import { Box, Checkbox, FormControl, FormHelperText, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FIELD, fieldLabelSx, fieldRootSx } from "./fieldTheme";

// List colours (blue)
const LIST = {
  accent: "#2563eb", // checkbox + "Select All" (blue)
  text: "#1d4ed8", // ticked option text
  rowBg: "#eff6ff", // ticked option background
  rowHoverBg: "#dbeafe",
  line: "#eef2f6", // thin line between options
  border: "#e5e7eb", // list + field border
};

export default function FilterMultiSelect({
    name,
    label,
    options = [],
    value = [], // array of option objects
    onChange,
    error = false,
    formHelperText = "",
    disabled = false,
}) {
    // Open the list right under the field (never on top of it). The list height is capped to the
    // space available below; only when there is very little room does it open above the field.
    const fieldRef = useRef(null);
    const [placement, setPlacement] = useState({ below: true, maxHeight: 280, width: 220 });
    const handleOpen = () => {
        const rect = fieldRef.current?.getBoundingClientRect();
        if (!rect) return;
        const gap = 16;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const below = spaceBelow >= 180 || spaceBelow >= spaceAbove;
        setPlacement({
            below,
            maxHeight: Math.max(120, Math.min(280, below ? spaceBelow : spaceAbove)),
            width: rect.width, // list matches the field width
        });
    };

    const selectedIds = Array.isArray(value) ? value.map((opt) => opt.value) : [];
    const isAllSelected = options.length > 0 && value.length === options.length;

    const handleChange = (e) => {
        const { value: newValue } = e.target;
        let updatedValues = [];

        if (newValue.includes("ALL")) {
            updatedValues = value.length === options.length ? [] : options; // toggle all
        } else {
            updatedValues = options.filter((opt) => newValue.includes(opt.value));
        }

        if (onChange) onChange(name, updatedValues);
    };

    const renderValue = (selectedVals) => {
        if (!selectedVals.length) {
            return <Box component="span" sx={{ color: FIELD.placeholder }}>None selected</Box>;
        }
        if (selectedVals.length === options.length) return `All (${options.length})`;
        const labels = options.filter((opt) => selectedVals.includes(opt.value)).map((opt) => opt.label);
        return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`;
    };

    const rowSx = (checked, isAllRow = false) => ({
        px: 1.5,
        py: 0.7,
        gap: 0.75,
        minHeight: 40,
        borderBottom: `1px solid ${LIST.line}`,
        bgcolor: checked && !isAllRow ? LIST.rowBg : "#fff",
        "&:hover": { bgcolor: checked && !isAllRow ? LIST.rowHoverBg : "#f8fafc" },
        "&.Mui-selected, &.Mui-selected.Mui-focusVisible": {
            bgcolor: isAllRow ? "#fff" : LIST.rowBg,
        },
        "&.Mui-selected:hover": { bgcolor: isAllRow ? "#f8fafc" : LIST.rowHoverBg },
        "& .MuiListItemText-root": { my: 0 },
        "& .MuiListItemText-primary": {
            fontSize: 13.5,
            fontWeight: checked || isAllRow ? 700 : 500,
            color: isAllRow ? LIST.accent : checked ? LIST.text : "#334155",
        },
    });

    const checkboxSx = {
        p: 0.25,
        color: "#bfdbfe",
        "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: LIST.accent },
        "& .MuiSvgIcon-root": { fontSize: 20 },
    };

    return (
        <FormControl fullWidth size="small" error={Boolean(error)} disabled={disabled}>
            {label && (
                <Typography component="label" htmlFor={`${name}-select`} sx={fieldLabelSx}>
                    {label}
                </Typography>
            )}
            <Select
                id={`${name}-select`}
                multiple
                displayEmpty
                ref={fieldRef}
                onOpen={handleOpen}
                value={selectedIds}
                onChange={handleChange}
                renderValue={renderValue}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                    ...fieldRootSx,
                    fontSize: 14,
                    "& .MuiSelect-select": { pl: 1.75, display: "flex", alignItems: "center" },
                    "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: LIST.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 4px rgba(37,99,235,0.10)" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#93c5fd" },
                    "& .MuiSelect-icon": { color: "#64748b", transition: "transform .2s ease" },
                    ...(error && {
                        "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#ef4444" },
                    }),
                }}
                MenuProps={{
                    autoFocus: false,
                    anchorOrigin: { vertical: placement.below ? "bottom" : "top", horizontal: "left" },
                    transformOrigin: { vertical: placement.below ? "top" : "bottom", horizontal: "left" },
                    PaperProps: {
                        sx: {
                            mt: placement.below ? 0.75 : 0,
                            mb: placement.below ? 0 : 0.75,
                            maxHeight: placement.maxHeight,
                            width: placement.width,
                            minWidth: 180,
                            borderRadius: 2.5,
                            border: `1px solid ${LIST.border}`,
                            boxShadow: "0 12px 28px -12px rgba(15,23,42,0.25)",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#cbd5e1 transparent",
                            "&::-webkit-scrollbar": { width: 6 },
                            "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 3 },
                        },
                    },
                    MenuListProps: { sx: { py: 0 } },
                }}
            >
                {/* "Select All" row */}
                <MenuItem
                    value="ALL"
                    sx={rowSx(isAllSelected, true)}
                >
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={value.length > 0 && !isAllSelected}
                        sx={checkboxSx}
                    />
                    <ListItemText primary="Select All" />
                </MenuItem>

                {options.map((opt) => {
                    const checked = selectedIds.includes(opt.value);
                    return (
                        <MenuItem key={opt.value} value={opt.value} sx={rowSx(checked)}>
                            <Checkbox checked={checked} sx={checkboxSx} />
                            <ListItemText primary={opt.label} primaryTypographyProps={{ noWrap: true, title: opt.label }} />
                        </MenuItem>
                    );
                })}
            </Select>
            {error && formHelperText && <FormHelperText>{formHelperText}</FormHelperText>}
        </FormControl>
    );
}
